import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Calendar, CheckCircle2, IndianRupee, Truck, Scissors, Footprints, Plus, ArrowRight, MapPin, Clock, Wallet, UserCircle, ShieldCheck } from "lucide-react";
import { Stat, NotPartner } from "./VetDashboard";
import QuickActions from "../components/QuickActions";

const TYPE_META = {
  groomer:        { label: "Groomer",  icon: Scissors, color: "bg-brand text-white" },
  walker:         { label: "Walker",   icon: Footprints, color: "bg-leaf text-ink" },
  mobile_vet_van: { label: "Mobile Vet Van", icon: Truck, color: "bg-sky text-white" },
};

export default function ServicesDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/partner/services/dashboard`).then(r => setData(r.data)).catch(() => setData("err"));
  useEffect(() => { load(); }, []);

  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <NotPartner />;

  const { providers, stats, upcoming_bookings, recent_bookings, visits } = data;
  const hasVan = providers.some(p => p.type === "mobile_vet_van");
  const primary = providers[0];
  const M = TYPE_META[primary.type];

  return (
    <div className="space-y-6" data-testid="services-dashboard-page">
      <header className="rounded-3xl bg-gradient-to-br from-ink to-[#1a1a1a] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-wider font-bold opacity-80">{M.label} Dashboard</div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Hi {user?.name?.split(" ")[0]}!</h1>
          <div className="opacity-90 text-sm">{primary.name} · {primary.city}</div>
        </div>
        <M.icon className="absolute right-6 top-6 w-16 h-16 opacity-20"/>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Calendar} label="Upcoming bookings" value={stats.upcoming_bookings} color="bg-brand/15 text-brand"/>
        <Stat icon={CheckCircle2} label="Completed" value={stats.completed_bookings} color="bg-leaf/30 text-ink"/>
        <Stat icon={IndianRupee} label="Revenue (total)" value={`₹${stats.revenue_total}`} color="bg-sun/30 text-ink"/>
        <Stat icon={Truck} label="Vans / Visits" value={`${stats.vans} / ${stats.scheduled_visits}`} color="bg-sky/15 text-sky"/>
      </div>

      <QuickActions
        testid="services-quick-actions"
        items={[
          ...(hasVan ? [{ to: "/app/van-schedule", icon: Truck, label: "Van schedule", desc: "Drop pins · open slots", color: "bg-sky/15 text-sky" }] : []),
          { to: "/app/payout-setup", icon: Wallet,      label: "Payouts setup", desc: "Bank · UPI · auto-settle", color: "bg-leaf/30 text-ink" },
          { to: "/app/profile", icon: UserCircle,  label: "Edit profile", desc: "Name · city · service area", color: "bg-sun/40 text-ink" },
          { to: "/app/kyc",     icon: ShieldCheck, label: "KYC",          desc: "Verify your business", color: "bg-ink/10 text-ink" },
        ]}
      />

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-xl font-bold mb-3">Upcoming bookings</h2>
        {upcoming_bookings.length === 0 ? (
          <p className="text-ink-soft text-sm">No upcoming bookings yet. Tell pet parents about your service!</p>
        ) : (
          <div className="divide-y divide-line">
            {upcoming_bookings.map(b => <BookingRow key={b.booking_id} b={b} onChange={load}/>)}
          </div>
        )}
      </section>

      {hasVan && (
        <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="van-ops">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2"><Truck className="w-5 h-5 text-sky"/>Mobile Vet Van schedule</h2>
            <Link to="/app/van-schedule" data-testid="schedule-van-visit" className="bg-sky text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1 hover:bg-sky-hover">
              <Plus className="w-4 h-4"/> Schedule a visit
            </Link>
          </div>
          {visits.length === 0 ? (
            <p className="text-ink-soft text-sm">No visits scheduled. Drop a pin on the map and announce a vaccination drive.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {visits.map(v => (
                <div key={v.visit_id} className="bg-surface-muted/50 rounded-2xl p-4">
                  <div className="font-heading font-extrabold">{v.location_label}</div>
                  <div className="text-xs text-ink-soft mt-1 inline-flex items-center gap-1"><Calendar className="w-3 h-3"/>{new Date(v.scheduled_at).toLocaleString()}</div>
                  <div className="text-xs text-ink-soft inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{v.city}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-leaf/40 rounded-full px-2 py-0.5">{v.slots_booked}/{v.total_slots} booked</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-surface-muted rounded-full px-2 py-0.5">{v.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-xl font-bold mb-3">Recent bookings</h2>
        {recent_bookings.length === 0 ? (
          <p className="text-ink-soft text-sm">No completed bookings yet.</p>
        ) : (
          <div className="divide-y divide-line">
            {recent_bookings.map(b => <BookingRow key={b.booking_id} b={b}/>)}
          </div>
        )}
      </section>
    </div>
  );
}

function BookingRow({ b, onChange }) {
  const set = async (status) => { await axios.patch(`${API}/services/bookings/${b.booking_id}`, { status }); onChange?.(); };
  return (
    <div className="py-3 flex items-center justify-between gap-3 text-sm flex-wrap">
      <div className="flex-1 min-w-[150px]">
        <div className="font-bold">{b.pet_name} <span className="text-ink-soft font-normal">· {b.service_type}</span></div>
        <div className="text-xs text-ink-soft inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{new Date(b.scheduled_at).toLocaleString()} · {b.user_name}</div>
      </div>
      <div className="text-sm font-heading font-extrabold">₹{b.amount}</div>
      {b.status === "pending" && onChange && <>
        <button onClick={() => set("confirmed")} data-testid={`confirm-${b.booking_id}`} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-bold">Confirm</button>
        <button onClick={() => set("cancelled")} className="bg-surface-muted text-ink-soft rounded-full px-3 py-1.5 text-xs font-bold">Decline</button>
      </>}
      {b.status === "confirmed" && onChange && <button onClick={() => set("completed")} className="bg-ink text-white rounded-full px-3 py-1.5 text-xs font-bold">Mark done</button>}
      {!onChange && <span className="text-[10px] font-bold uppercase tracking-wider bg-surface-muted rounded-full px-2 py-0.5">{b.status}</span>}
    </div>
  );
}
